import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Image as ImageIcon,
  Video,
  Grid2X2,
  Wand2,
  Loader2,
  ChevronDown,
  X,
  Bell,
} from 'lucide-react';
import {
  api,
  type JobStatus,
  type ModelConfig,
  announcementAPI,
  type Announcement,
  type GenerationMode,
  invitationAPI,
  type InvitationConfig,
  type HeroConfig,
} from './services/api';
import toast from './components/Toast';
import { AuthModal } from './components/AuthModal';
import { clearActiveAuthSession, savePrimaryAuthSession } from './utils/authSession';
import { ensureMediaToken } from './utils/mediaToken';
import { usePlatformSession } from './platform/session';
import { useFeatureFlags } from './hooks/useFeatureFlags';
import {
  Navbar,
  HeroSection,
  NotificationBanner,
  InviteBanner,
  ModeSelector,
  ReferenceImageUploader,
  PromptInput,
  SizeSelector,
  QualitySelector,
  BackgroundSelector,
  ImageCountSelector,
  ModelSelector,
  Lightbox,
  Canvas,
  RecentWorks,
  Footer,
  RedeemModal,
  type GeneratedImage,
  type PendingJob,
  type UserInfo,
} from './baselines/image';
import { getApiQuality, normalizeAspectRatioValue } from './baselines/image/utils';
import { resolveBrowserMediaUrl, withAuthToken } from './utils/storageUrl';
import { ASPECT_RATIOS, QUALITY_OPTIONS } from './baselines/image/constants';

const PROMPT_DRAFT_KEY = 'prompt_draft';
/** 最近一次生成成功的图片模型（浏览器记忆，下次进入默认选中） */
const IMAGE_LAST_USED_MODEL_KEY = 'image_last_used_model';

function resolveImagePromptRefSrc(raw: string): string {
  if (!raw) return raw;
  // /api/media/file 网关 URL 必须带短时媒体令牌，否则 <img> 会 401
  return withAuthToken(raw);
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'image'>('image');
  const [selectedModel, setSelectedModel] = useState('');
  const [generationMode, setGenerationMode] = useState<GenerationMode>('text2img');
  const [referenceImages, setReferenceImages] = useState<string[]>([]);
  const [maskImage, setMaskImage] = useState<string>('');
  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('auto');
  const [quality, setQuality] = useState('low');
  const [transparentBackground, setTransparentBackground] = useState(false);
  const [imageCount, setImageCount] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [currentImages, setCurrentImages] = useState<GeneratedImage[]>([]);
  const [currentImage, setCurrentImage] = useState<GeneratedImage | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showRedeemModal, setShowRedeemModal] = useState(false);

  // 页面加载时从 localStorage 恢复提示词草稿
  useEffect(() => {
    const savedDraft = localStorage.getItem(PROMPT_DRAFT_KEY);
    if (savedDraft) {
      setPrompt(savedDraft);
    }
  }, []);

  // 使用防抖自动保存提示词到 localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      if (prompt.trim()) {
        localStorage.setItem(PROMPT_DRAFT_KEY, prompt);
      } else {
        localStorage.removeItem(PROMPT_DRAFT_KEY);
      }
    }, 500); // 500ms 防抖

    return () => clearTimeout(timer);
  }, [prompt]);
  const [authModalMode, setAuthModalMode] = useState<'login' | 'register'>('login');
  const [, setPendingJobs] = useState<PendingJob[]>([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [hiddenAnnouncements, setHiddenAnnouncements] = useState<string[]>([]);
  const [announcementsCollapsed, setAnnouncementsCollapsed] = useState(false);
  const [autoCollapseDelayMs, setAutoCollapseDelayMs] = useState<number>(5000);
  const [siteName, setSiteName] = useState('AI 图片生成');
  const [copiedPromptImageId, setCopiedPromptImageId] = useState<string | null>(null);
  const [filledReferenceImageId, setFilledReferenceImageId] = useState<string | null>(null);
  const [inviteConfig, setInviteConfig] = useState<InvitationConfig | null>(null);
  const [heroConfig, setHeroConfig] = useState<HeroConfig>({
    siteName: 'AI 图片生成',
    heroTitle: '用文字创造惊艳图片',
    heroSubtitle: '支持文生图、参考图生图、局部重绘三种模式，基于 GPT Image 2 和 Gemini 模型',
    heroFeature1: '注册送 2 积分',
    heroFeature2: '支持中文',
    heroFeature3: '无需下载',
  });

  const {
    user,
    credits,
    isLoggedIn,
    isImpersonating,
    refreshUser,
    setUser,
    applyCreditsDelta,
    logout: sessionLogout,
  } = usePlatformSession();
  const [models, setModels] = useState<ModelConfig[]>([]);
  const { isEnabled: isFeatureEnabled } = useFeatureFlags();

  // 谱系记录：当前参考图的 job ID（用于迭代谱系）
  const [parentJobId, setParentJobId] = useState<string | null>(null);

  // 获取当前选中模型启用的模式配置
  const currentModel = models.find(m => m.id === selectedModel);
  const enabledModes = currentModel ? {
    text2img: currentModel.enableText2img,
    img2img: currentModel.enableImg2img,
    inpaint: currentModel.enableInpaint,
  } : {
    text2img: true,
    img2img: true,
    inpaint: true,
  };

  // 处理从迭代记录页面或作品页面返回的继续迭代状态
  useEffect(() => {
    const state = location.state as { continueIteration?: boolean; jobId?: string; imageUrl?: string; prompt?: string; mode?: 'img2img' | 'inpaint'; model?: string; size?: string; quality?: string } | null;
    if (state?.continueIteration && state.imageUrl) {
      // 设置生成模式（img2img 或 inpaint）
      setGenerationMode(state.mode || 'img2img');
      setReferenceImages([state.imageUrl]);
      setPrompt(state.prompt || '');

      // 记录父 job ID（用于迭代谱系）
      if (state.jobId) {
        setParentJobId(state.jobId);
      }

      // 清除 location state
      window.history.replaceState({}, document.title);
    }
    // 处理从图库页面跳转的同款生成（图生图模式）
    else if (state?.mode === 'img2img' && !state?.continueIteration) {
      setGenerationMode('img2img');
      setPrompt(state.prompt || '');
      if (state.model) setSelectedModel(state.model);
      if (state.size) setAspectRatio(normalizeAspectRatioValue(state.size));
      if (state.quality) setQuality(state.quality);
      // 清除 location state
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    api.getSiteName().then((res) => {
      setSiteName(res.siteName);
      document.title = res.siteName;
    }).catch(() => {});
  }, []);

  // 注入统计代码（Umami / Google Analytics 等）
  useEffect(() => {
    api.getAnalyticsTrackingCode().then(({ analyticsTrackingCode }) => {
      if (!analyticsTrackingCode) return;
      const container = document.createElement('div');
      container.innerHTML = analyticsTrackingCode;
      container.querySelectorAll('script').forEach((oldScript) => {
        const script = document.createElement('script');
        Array.from(oldScript.attributes).forEach((attr) => script.setAttribute(attr.name, attr.value));
        script.textContent = oldScript.textContent;
        document.head.appendChild(script);
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.getModels().then((res) => {
      setModels(res.data);
    }).catch(() => {});
  }, []);

  // 获取默认模型配置并设置
  useEffect(() => {
    const initDefaultModel = async () => {
      try {
        const [{ defaultModelId }, modelsRes] = await Promise.all([
          api.getDefaultModel(),
          api.getModels(),
        ]);
        setModels(modelsRes.data);

        // 优先恢复最近一次生成成功的模型；其次后台配置的默认模型；最后回退第一个
        const remembered = localStorage.getItem(IMAGE_LAST_USED_MODEL_KEY);
        if (remembered && modelsRes.data.some(m => m.id === remembered)) {
          setSelectedModel(remembered);
        } else if (defaultModelId && modelsRes.data.some(m => m.id === defaultModelId)) {
          // 使用后台配置的默认模型
          setSelectedModel(defaultModelId);
        } else if (modelsRes.data.length > 0) {
          // 回退到第一个可用模型
          setSelectedModel(modelsRes.data[0].id);
        }
      } catch {
        // 出错时使用第一个模型
        api.getModels().then((res) => {
          setModels(res.data);
          if (res.data.length > 0) {
            setSelectedModel(res.data[0].id);
          }
        }).catch(() => {});
      }
    };

    initDefaultModel();
  }, []);

  useEffect(() => {
    announcementAPI.getAnnouncements().then((res) => {
      setAnnouncements(res.data);
    }).catch(() => {});
  }, []);

  // 拉取公告自动折叠延时配置（0=不折叠）
  useEffect(() => {
    api.getAnnouncementAutoCollapseDelay().then((res) => {
      setAutoCollapseDelayMs(res.announcementAutoCollapseDelayMs ?? 5000);
    }).catch(() => {});
  }, []);

  // 开屏后到时把公告整体折叠（只触发一次）
  const autoCollapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (autoCollapseDelayMs <= 0) return;
    if (announcements.length === 0) return;

    const visibleIds = announcements
      .filter(a => !hiddenAnnouncements.includes(a.id))
      .map(a => a.id);
    if (visibleIds.length === 0) return;

    if (autoCollapseTimerRef.current) clearTimeout(autoCollapseTimerRef.current);
    autoCollapseTimerRef.current = setTimeout(() => {
      setAnnouncementsCollapsed(true);
    }, autoCollapseDelayMs);

    return () => {
      if (autoCollapseTimerRef.current) {
        clearTimeout(autoCollapseTimerRef.current);
        autoCollapseTimerRef.current = null;
      }
    };
    // 仅在首次拿到公告与延时时启动一次；用户后续手动展开不应被再次折叠
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announcements.length, autoCollapseDelayMs]);

  useEffect(() => {
    invitationAPI.getInvitationConfig().then((res) => {
      setInviteConfig(res);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.getHeroConfig().then((res) => {
      setHeroConfig(res);
    }).catch(() => {});
  }, []);

  // 检测邀请链接，自动弹出注册弹窗
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const invite = params.get('invite');
    if (invite && !isLoggedIn) {
      setAuthModalMode('register');
      setShowAuthModal(true);
    }
  }, [isLoggedIn]);

  // 处理 OAuth 登录回调
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthToken = params.get('oauth_token');
    const oauthSuccess = params.get('oauth_success');
    const oauthError = params.get('error');

    if (oauthToken && oauthSuccess === 'true') {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
      // token 落库必须留在页面层（Provider 不写 token）；refreshUser 内部已回写用户缓存
      savePrimaryAuthSession(null, oauthToken);
      void ensureMediaToken();
      void refreshUser().then((userInfo) => {
        if (userInfo) {
          toast.success('登录成功');
        } else {
          clearActiveAuthSession();
          toast.error('获取用户信息失败');
        }
      });
    } else if (oauthError) {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
      toast.error(decodeURIComponent(oauthError));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 可见性刷新与支付积分同步由 PlatformSessionProvider 统一监听

  const prevModeRef = useRef<GenerationMode>('text2img');
  useEffect(() => {
    // 如果当前模式被禁用，自动切换到第一个可用模式
    if (!enabledModes[generationMode]) {
      const availableModes = (['text2img', 'img2img', 'inpaint'] as GenerationMode[]).filter(m => enabledModes[m]);
      if (availableModes.length > 0 && availableModes[0] !== generationMode) {
        setGenerationMode(availableModes[0]);
        return;
      }
    }

    if (prevModeRef.current !== generationMode) {
      if (generationMode === 'text2img') {
        setReferenceImages([]);
        setMaskImage('');
      } else if (generationMode === 'inpaint' && referenceImages.length > 1) {
        setReferenceImages(prev => prev.slice(0, 1));
      }
      prevModeRef.current = generationMode;
    }
  }, [generationMode, enabledModes]);

  const convertJobToImage = useCallback((job: JobStatus): GeneratedImage => {
    let url = '';
    if (job.resultUrl) {
      url = job.resultUrl;
    }

    return {
      id: job.id,
      url,
      prompt: job.prompt,
      createdAt: new Date(job.createdAt),
      model: job.model,
      size: job.size,
      quality: job.quality,
      mode: job.mode,
      status: job.status,
      errorMessage: job.errorMessage,
      referenceImages: job.referenceImages,
    };
  }, []);

  const loadImages = useCallback(async () => {
    if (!isLoggedIn) return;
    try {
      const res = await api.getImageList();
      const imgs: GeneratedImage[] = (res.data || []).map(convertJobToImage);
      setGeneratedImages(imgs);
      if (currentImage) {
        const updated = imgs.find(i => i.id === currentImage.id);
        if (updated && updated.status === 'completed' && updated.url) {
          setCurrentImage(updated);
        }
      }
      // 同步 currentImages 中由"用户主动选中"的图（如果其状态后端有更新，例如缩略图就绪）
      setCurrentImages(prevCurrentImages => {
        if (prevCurrentImages.length === 0) return prevCurrentImages;
        const updatedCurrentImages = prevCurrentImages.map(img => {
          const updated = imgs.find(i => i.id === img.id);
          return updated || img;
        });
        const hasChanges = updatedCurrentImages.some((img, idx) =>
          img.id !== prevCurrentImages[idx].id ||
          img.status !== prevCurrentImages[idx].status ||
          img.url !== prevCurrentImages[idx].url
        );
        return hasChanges ? updatedCurrentImages : prevCurrentImages;
      });
    } catch {
      // silent
    }
  }, [isLoggedIn, currentImage?.id, convertJobToImage]);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  useEffect(() => {
    if (!isLoggedIn) return;
    const interval = setInterval(loadImages, 5000);
    return () => clearInterval(interval);
  }, [isLoggedIn, loadImages]);

  const selectedModelConfig = models.find(m => m.id === selectedModel);
  const availableQualityOptions = QUALITY_OPTIONS.filter((option) => {
    if (!selectedModelConfig) return true;
    if (option.value === 'low') return selectedModelConfig.enableStandardSize !== false;
    if (option.value === 'medium') return selectedModelConfig.enableHdSize !== false;
    if (option.value === 'high') return selectedModelConfig.enableUhdSize !== false;
    return true;
  });
  const availableQualityOptionsForMode = availableQualityOptions.filter((option) => {
    if (!selectedModelConfig?.qualityCapabilities) return true;
    return selectedModelConfig.qualityCapabilities[generationMode]?.[option.value as 'low' | 'medium' | 'high']?.enabled !== false;
  });
  const selectedQuality = availableQualityOptions.some(option => option.value === quality)
    && availableQualityOptionsForMode.some(option => option.value === quality)
    ? quality
    : availableQualityOptionsForMode[0]?.value || availableQualityOptions[0]?.value || 'low';
  const currentQualityCapability = selectedModelConfig?.qualityCapabilities?.[generationMode]?.[selectedQuality as 'low' | 'medium' | 'high'];
  const availableAspectRatioOptions = useMemo(() => {
    const supportedAspectRatios = currentQualityCapability?.supportedAspectRatios
      || selectedModelConfig?.modeCapabilities?.[generationMode]?.supportedAspectRatios;
    if (!supportedAspectRatios?.length) {
      return ASPECT_RATIOS;
    }
    const supportedSet = new Set(supportedAspectRatios);
    const filtered = ASPECT_RATIOS.filter((option) => supportedSet.has(option.value));
    return filtered.length > 0 ? filtered : ASPECT_RATIOS;
  }, [currentQualityCapability?.supportedAspectRatios, generationMode, selectedModelConfig]);
  const qualityCreditCost = selectedModelConfig?.credits[selectedQuality as keyof ModelConfig['credits']] ?? 1;
  const modeCreditCost = selectedModelConfig?.modeCredits?.[generationMode as keyof NonNullable<ModelConfig['modeCredits']>] ?? 0;
  const creditCostPerImage = qualityCreditCost + modeCreditCost;
  const totalCreditCost = creditCostPerImage * imageCount;

  useEffect(() => {
    if (availableAspectRatioOptions.some((option) => option.value === aspectRatio)) {
      return;
    }
    setAspectRatio(availableAspectRatioOptions[0]?.value || '1:1');
  }, [aspectRatio, availableAspectRatioOptions]);

  useEffect(() => {
    if (quality !== selectedQuality) {
      setQuality(selectedQuality);
    }
  }, [quality, selectedQuality]);

  // 模型不支持透明背景时自动关闭，避免带着失效参数发请求
  useEffect(() => {
    if (transparentBackground && selectedModelConfig?.enableTransparentBackground !== true) {
      setTransparentBackground(false);
    }
  }, [transparentBackground, selectedModelConfig]);

  const handleLoginSuccess = (u: UserInfo) => {
    setUser(u);
  };

  const handleLogout = () => {
    // 会话清理由 Provider 负责；页面只清生成结果（登出后不留上一账号的作品）
    sessionLogout();
    setGeneratedImages([]);
    setCurrentImage(null);
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    if (!isLoggedIn) {
      setShowAuthModal(true);
      return;
    }

    if ((generationMode === 'img2img' || generationMode === 'inpaint') && referenceImages.length === 0) {
      toast.error('请上传参考图');
      return;
    }

    if (generationMode === 'inpaint' && !maskImage) {
      toast.error('请绘制蒙版区域');
      return;
    }

    if (credits < totalCreditCost) {
      toast.error('积分不足，请购买积分');
      return;
    }

    setIsGenerating(true);
    setCurrentImages([]);

    try {
      const generatePromises = [];
      const newJobs: PendingJob[] = [];
      const pendingImages: GeneratedImage[] = [];
      const normalizedAspectRatio = normalizeAspectRatioValue(aspectRatio);

      for (let i = 0; i < imageCount; i++) {
        const promise = (async () => {
          const response = await api.generateImage({
            model: selectedModel,
            prompt,
            aspectRatio: normalizedAspectRatio,
            quality: getApiQuality(selectedQuality),
            n: 1,
            mode: generationMode,
            referenceImages: referenceImages.length > 0 ? referenceImages : undefined,
            maskImage: maskImage || undefined,
            moderation: 'low',
            background: transparentBackground && selectedModelConfig?.enableTransparentBackground === true
              ? 'transparent'
              : undefined,
            parentJobId: parentJobId || undefined,
          });

          const newJob: PendingJob = {
            id: response.jobId,
            prompt: prompt.substring(0, 30) + (prompt.length > 30 ? '...' : ''),
            status: 'processing',
            progress: 0,
          };
          newJobs.push(newJob);

          const pendingImage: GeneratedImage = {
            id: response.jobId,
            url: '',
            prompt,
            createdAt: new Date(),
            model: selectedModel,
            size: normalizedAspectRatio,
            quality: selectedQuality,
            mode: generationMode,
            status: 'processing',
          };
          pendingImages.push(pendingImage);

          setPendingJobs(prev => [...prev, newJob]);
          setGeneratedImages(prev => [pendingImage, ...prev]);
          // 注意：不再把生成中 / 完成的图片自动塞到 currentImages（画布）。
          // 画布仅在用户主动选最近作品 / 点导航按钮时才有内容。
          // 这样一来画布是"用户的浏览空间"，与"生成进度"解耦。

          api.pollJobStatus(
            response.jobId,
            async (job) => {
              setPendingJobs(prev =>
                prev.map(j =>
                  j.id === job.id
                    ? { ...j, status: job.status, progress: job.status === 'processing' ? 50 : 100 }
                    : j
                )
              );

              loadImages();
            }
          ).then(() => {
            setPendingJobs(prev => prev.filter(j => j.id !== response.jobId));
            loadImages();
          }).catch((error) => {
            console.error('Job failed:', error);
            toast.error('图片生成失败: ' + (error instanceof Error ? error.message : '未知错误'));
            setPendingJobs(prev => prev.filter(j => j.id !== response.jobId));
            loadImages();

            // 任务失败时，自动将提示词和参考图填充回表单
            // 保留用户的原始输入，方便修改后重新生成
            if (pendingImage.prompt) {
              setPrompt(pendingImage.prompt);
            }
            if (referenceImages.length > 0) {
              setReferenceImages(referenceImages);
            }
            // 保留生成模式、模型、尺寸、质量等参数不变
          });

          return response;
        })();

        generatePromises.push(promise);
      }

      await Promise.all(generatePromises);

      // 生成完成后清空相关状态
      setParentJobId(null);

      // 函数式扣积分，避免快速连续提交读到旧闭包余额
      applyCreditsDelta(-totalCreditCost);

      // 保留提示词：任务提交后即异步生成，用户可基于原提示词微调重出，
      // 草稿随之保留（防抖 effect 会自动同步），不再手动清除。
      setReferenceImages([]);
      setMaskImage('');

      // 记住本次生成使用的模型，下次进入默认选中
      localStorage.setItem(IMAGE_LAST_USED_MODEL_KEY, selectedModel);
    } catch (error) {
      console.error('Generation failed:', error);
      toast.error('生成失败: ' + (error instanceof Error ? error.message : '未知错误'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async (image?: GeneratedImage) => {
    const targetImage = image || currentImage;
    if (targetImage?.url) {
      try {
        // 私有媒体网关 URL 必须带短时令牌，否则 401
        const response = await fetch(withAuthToken(targetImage.url));
        const blob = await response.blob();

        // 创建 blob URL
        const blobUrl = window.URL.createObjectURL(blob);

        // 创建下载链接
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `generated-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // 释放 blob URL
        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error('下载失败:', error);
        // 降级方案：直接打开图片（同样需要令牌）
        window.open(withAuthToken(targetImage.url), '_blank');
      }
    }
  };

  const handleRefreshImages = async () => {
    await loadImages();
  };

  const handleDeleteImage = async (id: string) => {
    try {
      await api.deleteImage(id);
      setGeneratedImages(prev => prev.filter(img => img.id !== id));
      if (currentImage?.id === id) {
        setCurrentImage(null);
      }
    } catch (error) {
      console.error('隐藏图片失败:', error);
      toast.error('隐藏图片失败');
    }
  };

  const handleUseAsReference = (image?: GeneratedImage) => {
    const targetImage = image || currentImage;
    if (targetImage?.url && referenceImages.length < 16) {
      setGenerationMode('img2img');
      setReferenceImages(prev => {
        if (prev.length < 16 && !prev.includes(targetImage.url)) {
          return [...prev, targetImage.url];
        }
        return prev;
      });

      // 设置父 job ID（用于迭代谱系）
      setParentJobId(targetImage.id);
    }
  };

  const handleUseAsInpaint = (image?: GeneratedImage) => {
    const targetImage = image || currentImage;
    if (targetImage?.url) {
      setGenerationMode('inpaint');
      setReferenceImages([targetImage.url]);
      setMaskImage('');

      // 设置父 job ID（用于迭代谱系）
      setParentJobId(targetImage.id);
    }
  };

  const handleFillCurrentPrompt = (image?: GeneratedImage) => {
    const targetImage = image || currentImage;
    if (!targetImage?.prompt) return;

    setPrompt(targetImage.prompt);

    // 设置父 job ID（用于迭代谱系）
    setParentJobId(targetImage.id);

    setCopiedPromptImageId(targetImage.id);
    setTimeout(() => setCopiedPromptImageId(null), 2000);
  };

  const handleFillReference = (image?: GeneratedImage) => {
    const targetImage = image || currentImage;
    if (!targetImage?.referenceImages || targetImage.referenceImages.length === 0) return;

    if (targetImage.mode === 'inpaint') {
      setGenerationMode('inpaint');
      setReferenceImages([targetImage.referenceImages[0]]);
      setMaskImage('');
    } else {
      setGenerationMode('img2img');
      setReferenceImages(targetImage.referenceImages);
      setMaskImage('');
    }

    // 设置父 job ID（用于迭代谱系）
    setParentJobId(targetImage.id);

    setFilledReferenceImageId(targetImage.id);
    setTimeout(() => setFilledReferenceImageId(null), 2000);
  };

  return (
    <div
      className="min-h-screen bg-light-bg"
      style={{ paddingTop: '64px' }}
    >
      <Navbar
        siteName={siteName}
        onRedeemCredits={() => isLoggedIn ? setShowRedeemModal(true) : setShowAuthModal(true)}
        onLogin={() => setShowAuthModal(true)}
        onLogout={handleLogout}
      />

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleLoginSuccess}
        defaultMode={authModalMode}
      />

      <RedeemModal
        isOpen={showRedeemModal}
        onClose={() => setShowRedeemModal(false)}
        onSuccess={(newCredits: number) => {
          // 兑换是设置绝对余额并回写缓存
          setUser(user ? { ...user, credits: newCredits } : null);
        }}
      />



      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {isImpersonating && (
          <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            当前为代登录会话，正在以该用户身份访问。此会话仅当前标签页有效，退出后自动结束，不会影响管理员原登录状态。
          </div>
        )}

        {announcements.filter(a => !hiddenAnnouncements.includes(a.id)).length > 0 && (
          announcementsCollapsed ? (
            <div className="mb-4">
              <div className="bg-accent-blue/10 border border-accent-blue/30 rounded-2xl px-4 py-2 shadow-sm">
                <div className="flex items-center gap-2">
                  <Bell className="shrink-0 w-4 h-4 text-accent-blue" />
                  <span className="shrink-0 text-xs font-medium px-2 py-0.5 rounded-lg bg-accent-blue/20 text-accent-blue">
                    公告
                  </span>
                  <p className="flex-1 min-w-0 text-sm text-light-text truncate">
                    {announcements.filter(a => !hiddenAnnouncements.includes(a.id)).length} 条公告
                  </p>
                  <button
                    onClick={() => setAnnouncementsCollapsed(false)}
                    title="展开"
                    className="shrink-0 text-light-muted hover:text-light-text transition-colors p-1"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setHiddenAnnouncements(prev => Array.from(new Set([...prev, ...announcements.map(a => a.id)])))}
                    title="全部关闭"
                    className="shrink-0 text-light-muted hover:text-light-text transition-colors p-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-4 space-y-4">
              {announcements
                .filter(a => !hiddenAnnouncements.includes(a.id))
                .map(announcement => (
                  <NotificationBanner
                    key={announcement.id}
                    type={announcement.type}
                    label={announcement.label}
                    title={announcement.title}
                    content={announcement.content}
                    link={announcement.linkUrl ? { text: announcement.linkText || '查看详情', url: announcement.linkUrl } : undefined}
                    bannerImageUrl={announcement.bannerImageUrl}
                    onClose={() => setHiddenAnnouncements(prev => [...prev, announcement.id])}
                  />
                ))}
            </div>
          )
        )}

        <HeroSection config={heroConfig} />

        <InviteBanner
          config={inviteConfig}
          isLoggedIn={isLoggedIn}
          onLogin={() => setShowAuthModal(true)}
        />

        <div className="grid lg:grid-cols-[420px,1fr] gap-6">
          <div className="space-y-6">
            <div className="card p-1.5 flex gap-1">
              <button
                onClick={() => setActiveTab('image')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  activeTab === 'image' ? 'tab-active' : 'tab-inactive'
                }`}
              >
                <ImageIcon className="w-4 h-4" />
                图片
              </button>
              {isFeatureEnabled('videoEnabled') && (
                <button
                  onClick={() => navigate('/video')}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all tab-inactive"
                >
                  <Video className="w-4 h-4" />
                  视频
                </button>
              )}
              <button
                onClick={() => navigate('/apps')}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all tab-inactive"
              >
                <Grid2X2 className="w-4 h-4" />
                应用中心
              </button>
            </div>

            {activeTab === 'image' && (
              <div className="card p-5 space-y-6">
                <ModeSelector value={generationMode} onChange={setGenerationMode} enabledModes={enabledModes} />
                <ModelSelector value={selectedModel} onChange={setSelectedModel} models={models} />
                <ReferenceImageUploader
                  images={referenceImages}
                  onImagesChange={setReferenceImages}
                  mode={generationMode}
                  maxReferenceImages={selectedModelConfig?.qualityCapabilities?.img2img?.[selectedQuality as 'low' | 'medium' | 'high']?.referenceImageMaxCount ?? selectedModelConfig?.modeCapabilities?.img2img.referenceImageMaxCount ?? 16}
                  maskImage={maskImage}
                  onMaskChange={setMaskImage}
                />
                <PromptInput
                  value={prompt}
                  onChange={setPrompt}
                  mode={generationMode}
                  referenceImages={generationMode === 'img2img' ? referenceImages : undefined}
                  resolveImageSrc={resolveImagePromptRefSrc}
                />
                <SizeSelector
                  aspectRatio={aspectRatio}
                  onAspectRatioChange={setAspectRatio}
                  availableAspectRatios={availableAspectRatioOptions}
                />
                <QualitySelector value={selectedQuality} onChange={setQuality} model={selectedModelConfig} mode={generationMode} />
                <BackgroundSelector value={transparentBackground} onChange={setTransparentBackground} model={selectedModelConfig} />
                <ImageCountSelector value={imageCount} onChange={setImageCount} />

                <button
                  onClick={handleGenerate}
                  disabled={!prompt.trim() || isGenerating}
                  className="w-full btn-primary py-3 flex items-center justify-center gap-2 text-base shadow-lg"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      提交中...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-5 h-5" />
                      {isLoggedIn ? `生成${imageCount > 1 ? imageCount + '张' : ''}图片` : '登录后生成'}
                    </>
                  )}
                  {!isGenerating && isLoggedIn && (
                    <span className="text-sm opacity-70">
                      -{totalCreditCost}积分
                    </span>
                  )}
                </button>
              </div>
            )}

          </div>

          <div>
            <div className="card p-4 mb-4 flex items-center">
              <h3 className="text-sm font-semibold text-light-text">画布</h3>
            </div>
            {(() => {
              // 计算「画布单图模式下浏览最近作品」的上下切换：
              // - 只在画布显示单张图、且该图 ID 能在 generatedImages 里找到时启用
              // - 浏览池包含 completed（有 url）和 failed（无 url，但能展示失败占位 + 填充按钮）
              //   pending / processing / submitting 的占位卡跳过（避免切到一张转圈圈的）
              const browsable = generatedImages.filter(
                (g) =>
                  (g.status === 'completed' && !!g.url) ||
                  g.status === 'failed'
              );
              const inBrowse =
                currentImages.length === 1 &&
                browsable.some((g) => g.id === currentImages[0].id);
              const idx = inBrowse
                ? browsable.findIndex((g) => g.id === currentImages[0].id)
                : -1;
              const total = browsable.length;
              const navHandlers =
                inBrowse && total > 1
                  ? {
                      onPrevImage: () => {
                        const prev = browsable[(idx - 1 + total) % total];
                        if (prev) {
                          setCurrentImage(prev);
                          setCurrentImages([prev]);
                        }
                      },
                      onNextImage: () => {
                        const next = browsable[(idx + 1) % total];
                        if (next) {
                          setCurrentImage(next);
                          setCurrentImages([next]);
                        }
                      },
                      navIndex: idx,
                      navTotal: total,
                    }
                  : {};
              return (
                <Canvas
                  images={currentImages}
                  isLoading={false}
                  modelNameMap={new Map(models.map((m) => [m.id, m.name]))}
                  onImageClick={(image) => {
                    setCurrentImage(image);
                    if (image.url) {
                      setLightboxImage(resolveBrowserMediaUrl(image.url));
                      setLightboxOpen(true);
                    }
                  }}
                  onUseAsReference={handleUseAsReference}
                  onUseAsInpaint={handleUseAsInpaint}
                  onFillPrompt={handleFillCurrentPrompt}
                  onFillReference={handleFillReference}
                  onDownload={handleDownload}
                  filledPromptImageId={copiedPromptImageId}
                  filledReferenceImageId={filledReferenceImageId}
                  {...navHandlers}
                />
              );
            })()}
            <Lightbox
              image={lightboxImage}
              isOpen={lightboxOpen}
              onClose={() => {
                setLightboxOpen(false);
                setLightboxImage(null);
              }}
            />
            <RecentWorks
              images={generatedImages}
              onSelect={(image) => {
                setCurrentImage(image);
                setCurrentImages([image]);
              }}
              onRefresh={handleRefreshImages}
              onDelete={isLoggedIn ? handleDeleteImage : undefined}
            />
          </div>
        </div>
      </main>

      <Footer siteName={siteName} />
    </div>
  );
}

export default App;
